// ────────────────────────────────────────────────────────────────────
// markupLive — the frontend half of live co-markup (roadmap Phase 4B): a
// thin native-WebSocket client for server/routers/markup_ws.py's relay.
//
// Deliberately NOT wrapped in the axios instance (api.ts) — this is a raw
// WebSocket, not an HTTP call. Auth follows the SAME first-message pattern
// every other realtime surface in this app is forced into by one constraint:
// the browser WebSocket API cannot set custom headers, so a bearer JWT
// cannot ride an Authorization header the way liveEvents.ts's SSE reader
// sends it. Instead the first frame we send, right after `onopen`, IS the
// auth: `{"token": "...", "tenant": "..."}` — mirrors api.ts's own
// STAFF-sends-X-Tenant-Id / customer-never-does convention (see
// authPayload() below), just carried in the message body instead of a
// header because a WebSocket has no header to carry it in.
//
// Degrades to solo editing, never blocks the editor: a WS that never
// connects, or drops and fails its one reconnect attempt, simply means no
// `onRoster`/`onCursor`/`onShapeAdd` callback ever fires again — the caller
// (PhotoMarkup.tsx's MarkupModal) never sees an exception, never has to
// branch on "is live markup up right now?". Same posture as
// liveEvents.subscribeLiveEvents's silent no-op with no token.
//
// Voice 1:1 (roadmap D3, portal/src/lib/voiceCall.ts) rides this SAME
// socket/room via `call_offer`/`call_answer`/`call_ice`/`call_end`/
// `call_busy` frames — the two people on a call are, by construction, the
// same two people already in this markup room, so a second WS connection
// with its own auth+reconnect story would just duplicate this one. See
// server/routers/markup_ws.py's module docstring for the server-side half
// of that reasoning, including the honesty note on STUN-only NAT traversal.
// ────────────────────────────────────────────────────────────────────
import { getActiveTenant, getCachedUser, getToken, STAFF_ROLES } from "./api";
import type { MarkupShape } from "./types";

export interface RosterPeer {
  connId: string;
  name: string;
  color: string;
}

export interface MarkupLiveHandlers {
  onRoster?: (peers: RosterPeer[]) => void;
  onJoin?: (peer: RosterPeer) => void;
  onLeave?: (connId: string) => void;
  onCursor?: (peer: RosterPeer, x: number, y: number) => void;
  onShapeAdd?: (peer: RosterPeer, shape: MarkupShape, liveId: string | null) => void;
  onShapeRemove?: (liveId: string) => void;
  // ── Voice 1:1 signaling (roadmap D3) — see this file's module docstring
  // addendum below and server/routers/markup_ws.py's "Voice 1:1 signaling"
  // section for why this rides the SAME socket/room as markup, not a
  // separate connection. Handlers are optional: a caller that never wires
  // voiceCall.ts's useVoiceCall in just never gets these callbacks.
  onCallOffer?: (peer: RosterPeer, sdp: RTCSessionDescriptionInit) => void;
  onCallAnswer?: (peer: RosterPeer, sdp: RTCSessionDescriptionInit) => void;
  onCallIce?: (peer: RosterPeer, candidate: RTCIceCandidateInit) => void;
  onCallEnd?: (peer: RosterPeer) => void;
  /** The room's one call slot is already taken by two other people (or this
   *  frame's sender lost a race to answer first) — see markup_ws.py's
   *  `_handle_call_frame`. Carries no peer identity: the server sends it
   *  directly back to the frame's own sender, not as a relay. */
  onCallBusy?: () => void;
}

export interface MarkupLiveHandle {
  sendCursor: (x: number, y: number) => void;
  sendShapeAdd: (shape: MarkupShape, liveId: string) => void;
  sendShapeRemove: (liveId: string) => void;
  sendCallOffer: (sdp: RTCSessionDescriptionInit) => void;
  sendCallAnswer: (sdp: RTCSessionDescriptionInit) => void;
  sendCallIce: (candidate: RTCIceCandidateInit) => void;
  sendCallEnd: () => void;
  close: () => void;
}

/** Generates a short id for a locally-authored shape, so its later removal
 *  (undo) can be relayed unambiguously by id rather than by array position —
 *  a positional index would desync the moment either peer adds a shape out
 *  of turn. Not cryptographic, not persisted (see PhotoMarkup.tsx: stripped
 *  before JSON.stringify — the saved annotation schema is unchanged). Plain
 *  Math.random rather than crypto.randomUUID: this only needs to be unique
 *  within one open editor session, and avoids a Web Crypto dependency the
 *  test environment may not provide. */
export function genLiveId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function authPayload(): Record<string, unknown> {
  const payload: Record<string, unknown> = { token: getToken() ?? "" };
  // Same convention as api.ts's request interceptor / liveEvents.ts's
  // authHeaders(): only a STAFF caller viewing a chosen tenant sends one;
  // customers are tenant-locked server-side and must never send it.
  const tenant = getActiveTenant();
  const user = getCachedUser();
  if (tenant && user && (STAFF_ROLES as readonly string[]).includes(user.role)) {
    payload.tenant = tenant;
  }
  return payload;
}

function wsUrl(fileId: number): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/ws/markup/${fileId}`;
}

function isRosterPeer(x: unknown): x is RosterPeer {
  if (!x || typeof x !== "object") return false;
  const r = x as Record<string, unknown>;
  return typeof r.conn_id === "string" && typeof r.name === "string" && typeof r.color === "string";
}

function toRosterPeer(x: RosterPeer | Record<string, unknown>): RosterPeer {
  const r = x as Record<string, unknown>;
  return { connId: r.conn_id as string, name: r.name as string, color: r.color as string };
}

/** Connects to the live co-markup room for `fileId`. Silent no-op (no socket
 *  ever opened) when there's no auth token yet — mirrors
 *  liveEvents.subscribeLiveEvents's guard; a modal can only open once
 *  logged in, but the guard costs nothing and keeps the two realtime
 *  surfaces consistent. Reconnects exactly ONCE on an unexpected drop, then
 *  gives up silently — co-markup is a nice-to-have live layer, never a
 *  dependency the editor can be blocked on. */
export function connectMarkupLive(fileId: number, handlers: MarkupLiveHandlers): MarkupLiveHandle {
  const noop: MarkupLiveHandle = {
    sendCursor: () => { /* no-op: never connected */ },
    sendShapeAdd: () => { /* no-op: never connected */ },
    sendShapeRemove: () => { /* no-op: never connected */ },
    sendCallOffer: () => { /* no-op: never connected */ },
    sendCallAnswer: () => { /* no-op: never connected */ },
    sendCallIce: () => { /* no-op: never connected */ },
    sendCallEnd: () => { /* no-op: never connected */ },
    close: () => { /* no-op: never connected */ },
  };
  if (!getToken()) return noop;

  let ws: WebSocket | null = null;
  let closed = false;
  let reconnectedOnce = false;

  function handleMessage(ev: MessageEvent): void {
    let msg: unknown;
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return; // malformed frame — never throw out of the socket's event handler
    }
    if (!msg || typeof msg !== "object") return;
    const m = msg as Record<string, unknown>;
    switch (m.t) {
      case "roster": {
        const peers = Array.isArray(m.peers) ? m.peers.filter(isRosterPeer).map(toRosterPeer) : [];
        handlers.onRoster?.(peers);
        break;
      }
      case "join":
        if (isRosterPeer(m)) handlers.onJoin?.(toRosterPeer(m));
        break;
      case "leave":
        if (typeof m.conn_id === "string") handlers.onLeave?.(m.conn_id);
        break;
      case "cursor":
        if (isRosterPeer(m) && typeof m.x === "number" && typeof m.y === "number") {
          handlers.onCursor?.(toRosterPeer(m), m.x, m.y);
        }
        break;
      case "shape_add":
        if (isRosterPeer(m) && m.shape && typeof m.shape === "object") {
          const liveId = typeof m.live_id === "string" ? m.live_id : null;
          handlers.onShapeAdd?.(toRosterPeer(m), m.shape as MarkupShape, liveId);
        }
        break;
      case "shape_remove":
        if (typeof m.live_id === "string") handlers.onShapeRemove?.(m.live_id);
        break;
      case "call_offer":
        if (isRosterPeer(m) && m.sdp && typeof m.sdp === "object") {
          handlers.onCallOffer?.(toRosterPeer(m), m.sdp as RTCSessionDescriptionInit);
        }
        break;
      case "call_answer":
        if (isRosterPeer(m) && m.sdp && typeof m.sdp === "object") {
          handlers.onCallAnswer?.(toRosterPeer(m), m.sdp as RTCSessionDescriptionInit);
        }
        break;
      case "call_ice":
        if (isRosterPeer(m) && m.candidate && typeof m.candidate === "object") {
          handlers.onCallIce?.(toRosterPeer(m), m.candidate as RTCIceCandidateInit);
        }
        break;
      case "call_end":
        if (isRosterPeer(m)) handlers.onCallEnd?.(toRosterPeer(m));
        break;
      case "call_busy":
        handlers.onCallBusy?.();
        break;
      default:
        break; // unknown frame type — ignore, never throw
    }
  }

  function connect(): void {
    if (closed) return;
    const socket = new WebSocket(wsUrl(fileId));
    ws = socket;
    socket.onopen = () => {
      try {
        socket.send(JSON.stringify(authPayload()));
      } catch {
        /* send failing this early means the socket is already dead —
           onclose follows and drives the reconnect-once/give-up path */
      }
    };
    socket.onmessage = handleMessage;
    socket.onclose = () => {
      if (closed) return;
      if (!reconnectedOnce) {
        reconnectedOnce = true;
        connect();
      }
      // second drop: give up silently — see module docstring
    };
    // onerror carries no useful detail beyond what onclose already tells us;
    // the browser always follows an error with a close event.
  }
  connect();

  function trySend(obj: unknown): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) return; // degrade silently
    try {
      ws.send(JSON.stringify(obj));
    } catch {
      /* best-effort — a live cursor/shape frame is never worth surfacing an error for */
    }
  }

  return {
    sendCursor: (x, y) => trySend({ t: "cursor", x, y }),
    sendShapeAdd: (shape, liveId) => trySend({ t: "shape_add", shape, live_id: liveId }),
    sendShapeRemove: (liveId) => trySend({ t: "shape_remove", live_id: liveId }),
    sendCallOffer: (sdp) => trySend({ t: "call_offer", sdp }),
    sendCallAnswer: (sdp) => trySend({ t: "call_answer", sdp }),
    sendCallIce: (candidate) => trySend({ t: "call_ice", candidate }),
    sendCallEnd: () => trySend({ t: "call_end" }),
    close: () => {
      closed = true;
      ws?.close();
    },
  };
}
